import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { getMySalon } from "@/lib/salon.functions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Coins, LogOut, Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [
      { title: "Dashboard – SalonTurbo" },
      {
        name: "description",
        content: "Boost-Tage planen, Coins verwalten und Ergebnisse deiner Kampagnen einsehen.",
      },
      { property: "og:title", content: "Dashboard – SalonTurbo" },
      { property: "og:description", content: "Boost-Tage planen und Statistiken einsehen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: DashboardLayout,
});

function DashboardLayout() {
  const navigate = useNavigate();
  const fetchSalon = useServerFn(getMySalon);
  const { data, isLoading } = useQuery({ queryKey: ["my-salon"], queryFn: () => fetchSalon() });

  useEffect(() => {
    if (!isLoading && data && !data.salon?.onboarding_completed_at) {
      navigate({ to: "/onboarding" });
    }
  }, [data, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-secondary/30">
        <DashboardSidebar slug={data?.salon?.slug} />

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 items-center gap-3 border-b bg-background px-4">
            <SidebarTrigger />
            <div className="ml-auto flex items-center gap-3">
              <Badge variant="secondary" className="gap-1 py-1.5">
                <Coins className="size-3.5 text-primary" />
                {data?.coins ?? 0} Coins
              </Badge>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Abmelden"
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate({ to: "/auth" });
                }}
              >
                <LogOut className="size-4" />
              </Button>
            </div>
          </header>

          <main className="flex-1 px-4 py-8">
            <div className="mx-auto max-w-6xl">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

